import { useCallback, useEffect, useMemo, useState } from 'react'

import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  type BoxProps,
  FieldErrorText as EcFieldErrorText,
  FieldLabel as EcFieldLabel,
  FieldRoot,
  HStack,
  Input,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaArrowRightRegular, FaCheckRegular } from '@enechain/ecloud-icons'

import {
  type IssuerOption,
  buildIssuerOptions,
  buildRegisteredIssuerOptions,
  findIssuerOption,
  issuerAreaDeliveryContractCode,
  issuerTypeLabel,
} from '~/components/bidding/common/bidIssuer'
import { DsSelect } from '~/components/common/DsSelect'
import { RequiredMark } from '~/components/common/RequiredMark'
import { iconSize } from '~/constants/iconSize'
import { useListTradableAreas } from '~/hooks/bidding/useListTradableAreas'
import { JEPX_AREA_CODES, convertAreaLabel } from '~/models/area'

// chakra v3.29 + React 18 types の既知不具合で `FieldLabelProps` /
// `FieldErrorTextProps` が children を持たない型として解決される。
// `SpotBidCreateModal` と同じ回避で BoxProps に読み替える。
const FieldLabel = EcFieldLabel as React.FC<BoxProps & { htmlFor?: string }>
const FieldErrorText = EcFieldErrorText as React.FC<BoxProps>

type Props = {
  label?: string
  /**
   * 受渡日 (= モーダルを開いたページ・タブの受渡日)。 選択肢はこの日に有効な広域マスタで
   * 求める (保存時の受渡契約コード解決と同じ断面。 #8842)。
   */
  deliveryDate: Date
  /**
   * 選択中の発注主体区分。 未選択は `LicenseType.UNSPECIFIED`。 表示に使う値で、 未選択に戻す
   * 判断と既定選択の判断は `readValue` (省略時はこの値) で行う。
   */
  value: LicenseType
  /**
   * 選択された選択肢そのものを併せて渡す。 呼び出し側は区分ごとのエリアを引き直さずに
   * エリアの選択解除 (AC-BSP-005b / AC-BIN-004 ※) を判定できる。
   * 未選択に戻った場合は `option` が undefined。
   */
  onChange: (next: LicenseType, option: IssuerOption | undefined) => void
  /** 未選択のまま submit された等の入力エラー文言。 */
  errorMessage?: string
  /** 表示専用で開く場合 (= 締切後・下書き以外の札) に true。 */
  isDisabled?: boolean
  /**
   * 値を変えさせない場合 (= 既存の下書きの編集で、 保存済みの区分が選択肢にある間。 spec
   * AC-BSP-001a ※。 [[useIsSavedIssuerFixed]]) に true。 見た目は [[isDisabled]] と同じ。
   * 選択肢が一度も取得できていない間 (取得中・取得失敗) は、 保存済みの区分を戻さずそのまま固める。
   */
  isLocked?: boolean
  /**
   * 未選択の間、 選択肢の先頭 (= 優先順で最上位の区分) を選んだ状態にする。
   * spec AC-BSP-005d / AC-BIN-004 の既定選択「需要BG＞発電＞自己託送BG の優先順で
   * 登録済みのうち最優先の区分」。 **新規作成のときだけ** true にする — 既存の札の編集で
   * 札の区分が選択肢に無い時に、 別の区分へ黙って差し替えないため。
   */
  selectsDefault?: boolean
  /**
   * 親が持つ最新の区分の読み出し。 親が effect でフォームを書き換える (複製・再入札で元の札の
   * 区分を流し込む等) 場合は渡す。 `value` は描画時点の値なので、 書き換えた直後は古いことが
   * あり、 その `value` で判断すると流し込んだ区分を消す・需要BGに戻す。 未選択に戻す判断と
   * 既定選択の判断は、 どちらもこの値で行う。 省略時は `value` を使う。
   */
  readValue?: () => LicenseType
  /**
   * 見える項目名 (`FieldLabel`) を出さない。 開閉式フィルターのカードのように、 呼び出し側が
   * 「ラベル | 部品」 の格子でラベルを持つ時に使う (名前は select の aria-label に残る)。
   */
  hideLabel?: boolean
  /** 選べるエリアの文 ([[IssuerAreaStatus]]) を select の下に出さない。 呼び出し側が右に並べる時に使う。 */
  hideAreaStatus?: boolean
}

/**
 * 発注主体区分の選択肢 (受渡日時点)。 [[IssuerSelector]] 本体と同じ query cache を
 * 共有するので、 呼び出し側がエリア選択肢の絞り込みのために別途呼んでも追加の通信は
 * 起きない。
 */
export const useIssuerOptions = (deliveryDate: Date): IssuerOption[] => {
  // 取得失敗で入札画面ごと落とさない。 選択肢が引けないときは 0 件として
  // 「発注可能な発注主体区分が登録されていません」 の案内だけに縮退させる
  // (= 他の master 取得と同じ fail-soft。 `index.errorResilience.test.tsx`)。
  const { data } = useListTradableAreas(deliveryDate)
  return useMemo(() => buildIssuerOptions(data?.issuers ?? []), [data])
}

/** 登録済み区分を残し、対象日に有効なエリアが無い区分も空状態から選び直せる選択肢。 */
export const useRegisteredIssuerOptions = (
  deliveryDate: Date,
  options: { enabled?: boolean } = {},
): IssuerOption[] => {
  const { data, isPlaceholderData } = useListTradableAreas(deliveryDate, {
    retainsPreviousData: true,
    enabled: options.enabled,
  })
  return useMemo(
    () =>
      buildRegisteredIssuerOptions(
        data?.registeredIssuerTypes ?? [],
        isPlaceholderData ? [] : (data?.issuers ?? []),
      ),
    [data, isPlaceholderData],
  )
}

/** 登録済み区分の取得中は 0 件と断定しないための query 状態。 */
export const useRegisteredIssuerOptionsPending = (
  deliveryDate: Date,
  options: { enabled?: boolean } = {},
): boolean =>
  useListTradableAreas(deliveryDate, {
    retainsPreviousData: true,
    enabled: options.enabled,
  }).isPending

/**
 * 取得できた発注主体区分の選択肢。 取得できるまで (取得中・取得失敗) は undefined。
 * [[useIssuerOptions]] は取得できない間を 0 件に縮退させるので、 「0 件」 を根拠に下書きを
 * 一覧へ出す等の判断をする側が、 取得できていない間に誤って当てはめないために使う。
 */
export const useLoadedIssuerOptions = (
  deliveryDate: Date,
): IssuerOption[] | undefined => {
  const { data } = useListTradableAreas(deliveryDate)
  const options = useIssuerOptions(deliveryDate)
  return data === undefined ? undefined : options
}

/**
 * 発注主体区分の取得がまだ確定していない (= 初回ロード中) か。
 *
 * [[useIssuerOptions]] は fail-soft のため**取得中も取得失敗も 0 件**を返す。
 * 「1 件も登録されていない」 と 「まだ分からない」 を区別したい呼び出し側
 * (= 0 件を根拠に画面レベルで導線を塞ぐ側) が、 確定前に 0 件と断定しないために使う。
 * 取得失敗は従来どおり 0 件へ縮退する (= 確定はしているため false を返す)。
 */
export const useIssuerOptionsPending = (deliveryDate: Date): boolean =>
  useListTradableAreas(deliveryDate).isPending

/**
 * 既存の札の編集で、 保存済みの発注主体区分を固めたままにしてよいか (spec AC-BSP-001a ※ /
 * AC-BSP-135)。 `savedIssuerType` は開いた札の区分 (編集でなければ undefined)。 札の区分が選択肢に
 * ある間と、 選択肢が取得できるまで (取得中も取得失敗の間も) は true。 false の間は区分を選び直させる。
 *
 * 判断は選択中の区分ではなく開いた札の区分で行う。 選んだ直後に固めると、 札のエリアを扱えない
 * 区分を選んだ時に選び直せなくなる (エリアは編集で変えないため)。
 *
 * 取得失敗は選択肢が 0 件に縮退する ([[useIssuerOptions]]) ので、 選択肢の有無だけで判断すると
 * 失敗の間に固定が外れ、 開いたままの再取得で回復しても選び直しの状態が残る。
 */
export const useIsSavedIssuerFixed = (
  deliveryDate: Date,
  savedIssuerType: LicenseType | undefined,
  /** モーダルを開いている間 true。 閉じると選び直しの状態を忘れる。 */
  isActive = true,
): boolean => {
  const { data } = useListTradableAreas(deliveryDate)
  const options = useIssuerOptions(deliveryDate)
  const isSavedIssuerInOptions =
    data === undefined ||
    findIssuerOption(options, savedIssuerType ?? LicenseType.UNSPECIFIED) !==
      undefined
  // 一度選び直しに入ったら、 開いている間は戻さない。 再取得で保存済みの区分が選択肢に戻った時に
  // 固めると、 選び直した区分 (札のエリアを扱えないかもしれない) のまま変えられなくなる。
  const [hasStartedReselect, setHasStartedReselect] = useState(false)
  useEffect(() => {
    if (!isActive) {
      setHasStartedReselect(false)
      return
    }
    if (savedIssuerType !== undefined && !isSavedIssuerInOptions) {
      setHasStartedReselect(true)
    }
  }, [isActive, savedIssuerType, isSavedIssuerInOptions])
  if (savedIssuerType === undefined) return false
  return isSavedIssuerInOptions && !hasStartedReselect
}

/**
 * 発注主体区分の選択値を選択肢 (受渡日時点) に合わせ続ける。 [[IssuerSelector]] と
 * スポット入札の画面上部ツールバーの発注主体区分セレクタが共有する (区分の解決を 1 箇所に寄せる。
 * 選択肢・既定選択の規則が変わっても差し替えはここに閉じる)。
 *
 * - 選択肢に無い区分は未選択に戻す (編集で開いた札の区分が、 その受渡日の広域マスタでは
 *   解決できない時など)。 表示が「選択」 のまま区分が残ると、 見えていない区分で
 *   送信してしまう。 取得中は判断しない (= 確定前に消さない)。 表示専用は書き換えない
 * - `selectsDefault` の時、 未選択なら選択肢の先頭 (= 優先順で最上位) を選ぶ
 *   (AC-BSP-005d / AC-BIN-004)。 選択肢は非同期に届くので、 届いた時点でまだ未選択なら
 *   選ぶ。 値が空に戻った時も同じく選び直す
 */
type IssuerSelectionInput = {
  value: LicenseType
  onChange: (next: LicenseType, option: IssuerOption | undefined) => void
  isDisabled?: boolean
  selectsDefault?: boolean
  readValue?: () => LicenseType
  /** Preserve a saved issuer until date-specific options have actually loaded. */
  keepsValueUntilLoaded?: boolean
}

export const useIssuerSelection = ({
  deliveryDate,
  ...input
}: IssuerSelectionInput & { deliveryDate: Date }): {
  options: IssuerOption[]
  selected: IssuerOption | undefined
} => {
  const { data } = useListTradableAreas(deliveryDate)
  const options = useIssuerOptions(deliveryDate)
  const isPending = useIssuerOptionsPending(deliveryDate)
  return useIssuerSelectionWithOptions({
    ...input,
    options,
    isPending,
    isLoaded: data !== undefined,
  })
}

/** Common toolbar choices depend on registration, not delivery-date eligibility. */
export const useRegisteredIssuerSelection = ({
  deliveryDate,
  enabled,
  ...input
}: IssuerSelectionInput & { deliveryDate: Date; enabled?: boolean }): {
  options: IssuerOption[]
  selected: IssuerOption | undefined
} => {
  const { data, isPending } = useListTradableAreas(deliveryDate, {
    retainsPreviousData: true,
    enabled,
  })
  const options = useRegisteredIssuerOptions(deliveryDate, { enabled })
  return useIssuerSelectionWithOptions({
    ...input,
    options,
    isPending,
    isLoaded: data !== undefined,
  })
}

const useIssuerSelectionWithOptions = ({
  options,
  isPending,
  isLoaded,
  value,
  onChange,
  isDisabled = false,
  selectsDefault = false,
  readValue,
  keepsValueUntilLoaded = false,
}: IssuerSelectionInput & {
  options: IssuerOption[]
  isPending: boolean
  isLoaded: boolean
}): {
  options: IssuerOption[]
  selected: IssuerOption | undefined
} => {
  const selected = findIssuerOption(options, value)
  const currentIssuerType = useCallback(
    (): LicenseType => readValue?.() ?? value,
    [readValue, value],
  )

  useEffect(() => {
    if (isDisabled || isPending) return
    if (keepsValueUntilLoaded && !isLoaded) return
    const current = currentIssuerType()
    if (
      current === LicenseType.UNSPECIFIED ||
      findIssuerOption(options, current) !== undefined
    ) {
      return
    }
    onChange(LicenseType.UNSPECIFIED, undefined)
  }, [
    isDisabled,
    isPending,
    keepsValueUntilLoaded,
    isLoaded,
    options,
    currentIssuerType,
    onChange,
  ])

  const defaultOption = options[0]
  useEffect(() => {
    if (!selectsDefault || isDisabled) return
    if (currentIssuerType() !== LicenseType.UNSPECIFIED) return
    if (defaultOption === undefined) return
    onChange(defaultOption.issuerType, defaultOption)
  }, [selectsDefault, isDisabled, currentIssuerType, defaultOption, onChange])

  return { options, selected }
}

/**
 * 発注主体セレクター (= どの区分で JEPX に発注するかを選ぶ共通フォーム項目)。
 *
 * 通常入札 / 一括作成 / ブロック入札 / 時間前入札の
 * **すべての入札作成 UI がこの 1 本を使う** (CSV インポートは発注主体区分を行ごとの列で
 * 持つので使わない)。 以前は画面ごとに
 * 「事業者マスタ × ライセンスの 2 段 select」「ライセンス 4 固定のハードコード」
 * 「欄が無く発電に暗黙固定」 と 3 通りに分かれていた (issue #5366)。
 *
 * 選択肢は取引可能エリア RPC の区分ごとの応答 (受渡日時点の広域マスタ) から作る
 * ([[buildIssuerOptions]])。 選んだ区分はそのまま札の `issuer_type` になり、 受渡契約コードは
 * 保存時に区分 × エリアから backend が解決する。 ここでは区分ごとに「どのエリアのどの
 * コードで発注するか」 を示し、 エリア選択の絞り込みは呼び出し側が
 * [[issuerAreaAvailability]] で行う。
 */
export const IssuerSelector = ({
  label,
  deliveryDate,
  value,
  onChange,
  errorMessage,
  isDisabled = false,
  isLocked = false,
  selectsDefault = false,
  readValue,
  hideLabel = false,
  hideAreaStatus = false,
}: Props): React.ReactElement => {
  const { options } = useIssuerSelection({
    deliveryDate,
    value,
    onChange,
    isDisabled,
    selectsDefault,
    readValue,
    keepsValueUntilLoaded: isLocked,
  })
  return (
    <IssuerSelectorView
      errorMessage={errorMessage}
      hideAreaStatus={hideAreaStatus}
      hideLabel={hideLabel}
      isDisabled={isDisabled}
      isLocked={isLocked}
      label={label}
      onChange={onChange}
      options={options}
      value={value}
    />
  )
}

/** 対象日に有効なエリアが無い区分も残す作成画面用セレクター (AC-BIN-111)。 */
export const RegisteredIssuerSelector = ({
  label,
  deliveryDate,
  value,
  onChange,
  errorMessage,
  isDisabled = false,
  isLocked = false,
  selectsDefault = false,
  readValue,
}: Props): React.ReactElement => {
  const { options } = useRegisteredIssuerSelection({
    deliveryDate,
    value,
    onChange,
    isDisabled,
    selectsDefault,
    readValue,
    keepsValueUntilLoaded: isLocked,
  })
  return (
    <IssuerSelectorView
      errorMessage={errorMessage}
      isDisabled={isDisabled}
      isLocked={isLocked}
      label={label}
      onChange={onChange}
      options={options}
      value={value}
    />
  )
}

/** 選択肢の表示だけを担当し、フォームの値は変更しない。 */
export const IssuerSelectorView = ({
  label,
  value,
  onChange,
  errorMessage,
  isDisabled = false,
  isLocked = false,
  options,
  hideLabel = false,
  hideAreaStatus = false,
}: Omit<Props, 'deliveryDate' | 'selectsDefault' | 'readValue'> & {
  options: IssuerOption[]
}): React.ReactElement => {
  const { t } = useTranslation()
  const fieldLabel = label ?? t('page:biddingCommon.issuerSelector.label')
  const selected = findIssuerOption(options, value)
  // 固めるのは、 札の区分が選択肢にある間だけ。 選択肢に無い区分 (区分が空欄の取り込んだ札、
  // 受渡日の広域マスタで解決できない区分) は上で未選択に戻るので、 固めたままにすると
  // 必須の区分を選べず、 価格だけ直したい編集も保存できない。
  const isSelectLocked = isLocked && selected !== undefined

  return (
    <FieldRoot invalid={errorMessage !== undefined} required>
      {/* 発注主体は schema 上つねに必須。 他の必須項目と見た目を揃えるため
          ラベルに `*` を出す (2026-08-22 デザイナー判断「揃えたい」)。
          表示専用 (isDisabled) の時は出さない — 入力できない状態で必須を促しても
          意味が無いため。 */}
      {!hideLabel && (
        <FieldLabel>
          {fieldLabel}
          {!isDisabled && !isSelectLocked && <RequiredMark />}
        </FieldLabel>
      )}
      {options.length === 0 ? (
        // 発注主体区分が 1 件も無いと発注そのものができない。 黙って空のプルダウンを
        // 出さず、 何をすれば解消するかを示す。 登録画面への動線は出さない —
        // 広域マスタの登録は管理者の操作で、 この画面から登録できないため。
        <Text color="Text.midEmphasis" fontSize="sm">
          {t('page:biddingCommon.issuerSelector.noAccount')}
        </Text>
      ) : (
        <>
          {/* 単一 select の正は DS `Select` (2026-08-05 デザイナー確定)。
              ブラウザ標準の select は見た目・caret が OS 依存で他の入力欄と揃わない
              (2026-08-24 デザイナー指摘「発注主体選択時の UI は 8080 参照」)。
              発注できるエリアは select の下に置く (縦積みのフォーム項目)。 */}
          <HStack
            alignItems="stretch"
            flexDirection="column"
            alignSelf="stretch"
            gap={0}
          >
            {isSelectLocked ? (
              <Input
                aria-label={fieldLabel}
                readOnly
                value={issuerTypeLabel(selected.issuerType)}
              />
            ) : (
              <DsSelect
                flexShrink={0}
                width="100%"
                aria-label={fieldLabel}
                isDisabled={isDisabled}
                onChange={(next) => {
                  const issuerType =
                    next === undefined || next === ''
                      ? LicenseType.UNSPECIFIED
                      : (Number(next) as LicenseType)
                  onChange(issuerType, findIssuerOption(options, issuerType))
                }}
                options={options.map((o) => ({
                  value: String(o.issuerType),
                  label: issuerTypeLabel(o.issuerType),
                }))}
                placeholder={t('common:form.selectPlaceholder')}
                value={
                  selected !== undefined ? String(selected.issuerType) : ''
                }
              />
            )}
            {!hideAreaStatus && <IssuerAreaStatus selected={selected} />}
          </HStack>
        </>
      )}
      <FieldErrorText>{errorMessage}</FieldErrorText>
    </FieldRoot>
  )
}

/**
 * 選択中の発注主体区分で発注できるエリアの表示 (design `bid-issuer-selector-v2.js` の
 * `bid-issuer-status` 相当)。 並べるのは選べるエリア (`tradableAreas`、 JEPX 取引対象) だけで、
 * 登録済みでも選べないエリア (JEPX 会員情報が未設定・送信先の受渡契約コードが無い等) は
 * 「発注できます」 に含めない (spec AC-BSP-004 ※ / AC-BIN-004 ※ は、 その状態の選べるエリアを
 * 0 件と定める)。 選べるエリアが 1 つも無い区分 (例: 会員情報が未設定、 登録済みが沖縄だけ) は
 * この行を出さない。
 *
 * 見た目は UI 辞書 components/bid-issuer-selector.md の解決済みの表示 (`.bid-issuer-resolved`) と 8080:
 * 矢印 (`Text.lowEmphasis`) + チェック (`Text.positiveMid`) + エリアの文 (14px / `Text.midEmphasis`)。
 * icon の色も辞書の値 (`--text-low-emphasis` / `--text-positive-mid`) のまま Text 系を使う。
 * icon は 14px で、 文が 2 行に折れた時も 1 行目に載せる (高さ 1 行ぶんの箱の中央に置く)。
 */
export const IssuerAreaStatus = ({
  selected,
  marginTop = 1,
}: {
  selected: IssuerOption | undefined
  /** モーダルは select の下に置くので間を空ける。 ツールバーは select の右に並べるので 0。 */
  marginTop?: number
}): React.ReactElement | null => {
  const { t } = useTranslation()
  if (selected === undefined) return null

  // design 8080 と同じく「エリア名 (受渡契約コード)」 で併記する
  // (2026-08-24 デザイナー指摘)。 コードはエリアコードではなく、 需要BG / 自己託送BG なら
  // BG コード、 発電なら計画提出者コードで、 どの名義で発注するかの確認に使う。 コードは
  // 受渡契約コードを解決できたエリアの一覧 (`registeredAreas`) から引き、 見つからない・空の
  // エリアはエリア名だけ出す (括弧だけ空で出さない)。 JEPX 取引対象外のエリア (沖縄) は出さない。
  //
  // エリア名とコードの間は NBSP (U+00A0)。 半角スペースだと改行の位置になり、 文が 2 行に回った時に
  // 「中部」 / 「(LA004)」 が別の行に割れる (bid-issuer-selector.md「status の折返しは項目単位」、
  // 8080 `js/bid-issuer-selector-v2.js` の areaWithCode)。 見た目・幅は半角スペースと同じ。
  // エリアの間の区切りは辞書から引く (日本語「・」 / 英語「 / 」 = 8080 の英訳と同じ)。
  const areaNames = JEPX_AREA_CODES.filter((area) =>
    selected.tradableAreas.includes(area),
  )
    .map((area) => {
      const label = convertAreaLabel(area)
      const code = issuerAreaDeliveryContractCode(selected, area)
      return code !== undefined ? `${label}\u00a0(${code})` : label
    })
    .join(t('page:biddingCommon.issuerSelector.areaSeparator'))
  if (areaNames === '') return null

  // 高さ 1 行ぶんの箱。 icon をその中央に置き、 文が折れても 1 行目に揃える。
  const iconSlot = {
    flexShrink: 0,
    alignItems: 'center',
    display: 'inline-flex',
    height: '1lh',
  } as const

  return (
    <HStack alignItems="flex-start" gap={1} marginTop={marginTop} fontSize="md">
      {/* 矢印の左右 4px (`.bid-issuer-arrow { margin: 3px var(--space-1) 0 }`)。 チェックまで 8px になる */}
      <Box {...iconSlot} color="Text.lowEmphasis" marginX={1}>
        <FaArrowRightRegular boxSize={iconSize.fieldMessage} aria-hidden />
      </Box>
      <Box {...iconSlot} color="Text.positiveMid">
        <FaCheckRegular boxSize={iconSize.fieldMessage} aria-hidden />
      </Box>
      {/* keep-all はこの文にだけ掛ける (文字単位の改行を止め、「・」 と空白だけを改行の位置に残す。
          bid-issuer-selector.md 「keep-all はエリア列にだけ」)。 */}
      <Text minWidth={0} color="Text.midEmphasis" wordBreak="keep-all">
        {t('page:biddingCommon.issuerSelector.tradableAreas', {
          areas: areaNames,
        })}
      </Text>
    </HStack>
  )
}
